import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import Header from "../component/Header";
import Footer from "../component/Footer";
import ProductCard from "../component/ProductCard";
import CircleSkeleton from "../component/CircleSkeleton";
import ProductCardSkeleton from "../component/ProductCardSkeleton";
import HeadingSkeleton from "../component/HeadingSkeleton";
import HeartIcon from "../component/HeartIcon";

import $ from "jquery";
import { API_BASE_URL, IMAGE_URL } from "../utils/app_url";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

import wishlistImg from "../assets/img/wishlist-icon2.svg";
import cartImg from "../assets/img/cart-icon.svg";
import plusIcon from "../assets/img/plus-icon.svg";
import minusIcon from "../assets/img/minus-icon.svg";

const ProductDetails = () => {
  const { CatalogId } = useParams();
  const navigate = useNavigate();
  const { user, setCartCount, storeData } = useAuth();
  const { selectedLanguage, langJson } = useLanguage();
  const [openLogin, setOpenLogin] = useState(false);

  const [openAccordion, setOpenAccordion] = useState("shipping");

  const [product, setProduct] = useState(null);
  const [activeVariant, setActiveVariant] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const [errorText, setErrorText] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedOthersProducts, setRelatedOthersProducts] = useState([]);
  const [recentlyViewedProducts, setRecentlyViewedProducts] = useState([]);
  const [crossSelling, setCrossSelling] = useState([]);
  const [isAuthorized, setIsAuthorized] = useState(true);

  const [wishBtn, setWishBtn] = useState(false);
  const [wishMsg, setWishMsg] = useState(null);
  const [openModal, setOpenModal] = useState();
  const [currentWishlistId, setCurrentWishlistId] = useState(null);

  const [zoomModalOpen, setZoomModalOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomImageIndex, setZoomImageIndex] = useState(0);
  const [activeVideoIndex, setActiveVideoIndex] = useState(null);

  const sliderRef = useRef(null);
  const thumbRef = useRef(null);
  const zoomImageRef = useRef(null);

  const getWishlistIdFromVariant = (variant, baseProduct = product) =>
    variant?.wishlist_id ?? baseProduct?.wishlist_id ?? null;

  /* ------------------------
     FETCH PRODUCT
  ------------------------ */
  const fetchData = async () => {
    setLoading(true);
    try {
      const requestHeaders = {
        "Content-Type": "application/json",
      };
      if (user?.authorisation?.token) {
        requestHeaders.Authorization = `Bearer ${user.authorisation.token}`;
      }

      const response = await fetch(
        `${API_BASE_URL}frontend/products/${CatalogId}`,
        {
          method: "POST",
          body: JSON.stringify({
            lang: selectedLanguage,
            user_id: user?.id,
          }),
          headers: requestHeaders,
        }
      );

      const res = await response.json();

      if (!res.res || !res.data) {
        setErrorText(res.msg || langJson?.productnotfound || "Product not found");
        setProduct(null);
        return;
      }

      const productData = res.data.product;

      // Check vendor authorization - vendors can only view their own products
      if (user?.role === "vendor") {
        const productStoreId = productData?.store?.id || productData?.store_id;
        const vendorStoreId = user?.store;

        // Block if store IDs don't match or vendor doesn't have a store
        if (!vendorStoreId || !productStoreId || Number(productStoreId) !== Number(vendorStoreId)) {
          setIsAuthorized(false);
          setErrorText(langJson?.notauthorized || "You are not authorized to view this product");
          setProduct(null);
          setLoading(false);
          return;
        }
      }

      setIsAuthorized(true);
      setProduct(productData);
      setRelatedProducts(res.data.related_products || []);
      setRelatedOthersProducts(res.data.related_other_products || []);
      setRecentlyViewedProducts(res.data.recently_viewed || []);
      setCrossSelling(res.data.cross_selling || []);

      if (productData?.variants?.length) {
        setActiveVariant(productData.variants[0]);
        setCurrentWishlistId(getWishlistIdFromVariant(productData.variants[0], productData));
        setActiveImageIndex(0);

        const stockInit = productData.variants[0].stocks.map(s => ({
          size: s.size,
          qty: 0,
          stock: s.stock,
        }));
        setSelectedItems(stockInit);
      }
    } catch (err) {
      console.error(err);
      setErrorText(langJson?.somethingwentwrong || "Something went wrong");
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [CatalogId, user]);

  /* ------------------------
     IMAGE SLIDER SETTINGS
  ------------------------ */
  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    afterChange: i => setActiveImageIndex(i),
  };

  const thumbSettings = {
    dots: false,
    infinite: false,
    vertical: true,
    slidesToShow: 4,
    slidesToScroll: 1,
    focusOnSelect: true,
  };

  /* ------------------------
     VARIANT CHANGE
  ------------------------ */
  const handleVariantChange = variant => {
    setActiveVariant(variant);
    setCurrentWishlistId(getWishlistIdFromVariant(variant));
    setActiveImageIndex(0);

    const stockReset = variant.stocks.map(s => ({
      size: s.size,
      qty: 0,
      stock: s.stock,
    }));
    setSelectedItems(stockReset);

    sliderRef.current?.slickGoTo(0);

    // Navigate to variant page using ID
    if (variant?.id) {
      navigate(`/product/${variant.id}`, { replace: true });
    }
  };

  /* ------------------------
     QUANTITY HANDLERS
  ------------------------ */
  const updateQty = (index, delta) => {
    setSelectedItems(prev => {
      const next = [...prev];
      const item = next[index];
      if (!item) return prev;

      const newQty = item.qty + delta;
      if (newQty < 0) return prev;

      // For 'order' stock_type, allow unlimited quantity. For 'in_stock', limit to available stock.
      if (product?.stock_type === 'in_stock' && newQty > item.stock) return prev;

      next[index] = { ...item, qty: newQty };
      return next;
    });
  };

  /* ------------------------
     ADD TO CART
  ------------------------ */
  const handleAddCart = async () => {
    if (!user) {
      $(".loginLink").click();
      return;
    }

    const payload = selectedItems
      .filter(i => i.qty > 0)
      .map(i => ({ size: i.size, order: i.qty }));

    if (!payload.length) {
      setErrorText(langJson?.pleaseselectatleastoneitem);
      return;
    }

    setLoading(true);
    setErrorText(null);

    try {
      const response = await fetch(`${API_BASE_URL}cart/add`, {
        method: "POST",
        body: JSON.stringify({
          visitor_order: payload,
          product_id: activeVariant.product_id,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.authorisation.token}`,
        },
      });

      const res = await response.json();
      setCartCount(res?.data?.cart_count || 0);
      setErrorText(res.msg);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // const handleAddWishlist = async () => {
  //   if (!user) {
  //     $(".loginLink").click();
  //     return;
  //   }

  //   setWishBtn(true);
  //   setWishMsg(null);

  //   try {
  //     const response = await fetch(`${API_BASE_URL}frontend/wishlists`, {
  //       method: "POST",
  //       body: JSON.stringify({
  //         product_id: activeVariant.product_id, // ✅ VARIANT SKU
  //         lang: selectedLanguage,
  //       }),
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `Bearer ${user.authorisation.token}`,
  //       },
  //     });

  //     const res = await response.json();
  //     setWishMsg(res.msg);
  //   } catch (e) {
  //     console.error(e);
  //   } finally {
  //     setWishBtn(false);
  //   }
  // };
  const handleAddWishlist = async (productId) => {
    if (!user) {
      $(".loginLink").click(() => {
        $(".loginPopup").addClass("active");
      });
      setOpenModal(!openModal);
    } else {
      setWishMsg(null);
      setWishBtn(true);
      try {
        const response = await fetch(`${API_BASE_URL}frontend/wishlists`, {
          method: "POST",
          body: JSON.stringify({
            product_id: productId,
            lang: selectedLanguage,
          }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.authorisation?.token}`,
          },
        });
        const responseData = await response.json();
        if (responseData.res === true) {
          setWishMsg(responseData.msg);
          NotificationManager.success(responseData.msg);
          setWishBtn(false);
          let wishlistId =
            responseData?.data?.wishlist?.id ??
            responseData?.data?.wishlist_id ??
            responseData?.data?.id ??
            responseData?.wishlist_id ??
            null;

          if (!wishlistId) {
            try {
              const refreshResponse = await fetch(`${API_BASE_URL}frontend/products/${CatalogId}`, {
                method: "POST",
                body: JSON.stringify({
                  lang: selectedLanguage,
                  user_id: user?.id,
                }),
                headers: {
                  "Content-Type": "application/json",
                  ...(user?.authorisation?.token
                    ? { Authorization: `Bearer ${user.authorisation.token}` }
                    : {}),
                },
              });
              const refreshData = await refreshResponse.json();
              const refreshedProduct = refreshData?.data?.product;
              const refreshedVariant = refreshedProduct?.variants?.find(
                variant => Number(variant?.product_id) === Number(productId)
              );
              wishlistId =
                refreshedVariant?.wishlist_id ??
                refreshedProduct?.wishlist_id ??
                null;
            } catch (error) {
            }
          }

          return wishlistId;
        } else {
          setWishMsg(responseData.msg);
          NotificationManager.warning(responseData.msg);
          setWishBtn(false);
          return null;
        }
        setTimeout(() => {
          setWishMsg(null);
        }, 5000);
      } catch (error) {
        console.error("Error during Sign UP:", error);
        setWishBtn(false);
        return null;
      }
    }
  };

  const handleRemoveWishlist = async wishlistId => {
    if (!wishlistId || !user) return false;

    try {
      const response = await fetch(`${API_BASE_URL}frontend/wishlists/${wishlistId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${user?.authorisation?.token}`,
        },
      });
      const responseData = await response.json();

      if (!responseData?.res) {
        NotificationManager.warning(responseData?.msg);
        return false;
      }

      return true;
    } catch (error) {
      NotificationManager.warning(langJson?.somethingwentwrong || "Something went wrong");
      return false;
    }
  };

  const addWishlistAndUpdate = async (productId, listSetter) => {
    const wishlistId = await handleAddWishlist(productId);
    if (!wishlistId) return;

    listSetter(prevList =>
      (prevList || []).map(item =>
        item.product_id === productId || item.colors?.some(color => color.product_id === productId)
          ? { ...item, wishlist_id: wishlistId }
          : item
      )
    );
  };

  const removeWishlistAndUpdate = async (wishlistId, listSetter) => {
    const ok = await handleRemoveWishlist(wishlistId);
    if (!ok) return;

    listSetter(prevList =>
      (prevList || []).map(item =>
        Number(item?.wishlist_id) === Number(wishlistId)
          ? { ...item, wishlist_id: null }
          : item
      )
    );
  };

  const handleToggleCurrentWishlist = async () => {
    if (!activeVariant?.product_id) return;

    if (currentWishlistId) {
      const ok = await handleRemoveWishlist(currentWishlistId);
      if (!ok) return;

      setCurrentWishlistId(null);
      setActiveVariant(prev => (prev ? { ...prev, wishlist_id: null } : prev));
      setProduct(prev => {
        if (!prev?.variants) return prev;
        return {
          ...prev,
          wishlist_id: null,
          variants: prev.variants.map(variant =>
            Number(variant?.product_id) === Number(activeVariant?.product_id)
              ? { ...variant, wishlist_id: null }
              : variant
          ),
        };
      });
      return;
    }

    const wishlistId = await handleAddWishlist(activeVariant.product_id);
    if (!wishlistId) return;

    setCurrentWishlistId(wishlistId);
    setActiveVariant(prev => (prev ? { ...prev, wishlist_id: wishlistId } : prev));
    setProduct(prev => {
      if (!prev?.variants) return prev;
      return {
        ...prev,
        wishlist_id: wishlistId,
        variants: prev.variants.map(variant =>
          Number(variant?.product_id) === Number(activeVariant?.product_id)
            ? { ...variant, wishlist_id: wishlistId }
            : variant
        ),
      };
    });
  };

  const handleRequestPrice = async (storeId) => {
    if (!user) {
      setOpenLogin(true);
    }

    const res = await fetch(`${API_BASE_URL}frontend/request-price`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user.authorisation.token}`,
      },
      body: JSON.stringify({
        store_id: storeId,
        lang: selectedLanguage,
      }),
    });

    const data = await res.json();
    data.res
      ? NotificationManager.success(data.msg)
      : NotificationManager.warning(data.msg);
  };

  return (
    <div>
      <Header />
      <style>{`body { background: #FFF; }`}</style>

      <div className="mainDiv">
        <div className="manPrdtDtls">
          {loading ? (
            <>
              {/* Store Skeleton */}
              <div className="fairTop fairStore">
                <div className="fairLft singleLogo">
                  <div className="logoLft">
                    <CircleSkeleton size="100px" />
                  </div>
                  <div className="logoRgt">
                    <div className="skeleton" style={{ width: "200px", height: "24px" }} />
                  </div>
                </div>
              </div>

              {/* Product Details Skeleton */}
              <div className="topDetails prdtDtlsNew">
                {/* LEFT - Image Skeleton */}
                <div className="leftColumn">
                  <div className="product-detail">
                    <div className="mainImg">
                      <div className="skeleton" style={{ width: "100%", height: "500px" }} />
                    </div>
                    <div className="thumbImg">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="thumbnail skeleton" style={{ width: "102px", height: "122px", marginBottom: "10px" }} />
                      ))}
                    </div>
                  </div>
                </div>

                {/* RIGHT - Details Skeleton */}
                <div className="rightColumn">
                  <HeadingSkeleton width="300px" height="32px" />

                  {/* Variants */}
                  <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <CircleSkeleton size="45px" />
                    ))}
                  </div>

                  {/* Stock Table */}
                  <div style={{ marginTop: "30px" }}>
                    <div className="skeleton" style={{ width: "100%", height: "40px", marginBottom: "10px" }} />
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="skeleton" style={{ width: "100%", height: "50px", marginBottom: "8px" }} />
                    ))}
                  </div>

                  {/* Buttons */}
                  <div style={{ display: "flex", gap: "15px", marginTop: "30px" }}>
                    <div className="skeleton" style={{ width: "48%", height: "50px" }} />
                    <div className="skeleton" style={{ width: "48%", height: "50px" }} />
                  </div>

                  {/* Description */}
                  <div style={{ marginTop: "30px" }}>
                    <div className="skeleton" style={{ width: "100%", height: "24px" }} />
                  </div>
                </div>
              </div>

              {/* Related Products Skeleton */}
              <div className="tradePrdts">
                <div className="featureDiv relatedDiv topNew">
                  <HeadingSkeleton width="200px" height="28px" />
                  <div className="productList">
                    <ul className="products new-products">
                      {Array.from({ length: 4 }).map((_, index) => (
                        <ProductCardSkeleton key={index} />
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </>
          ) : errorText ? (
            <div style={{ padding: "40px", textAlign: "center", minHeight: "400px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div>
                <h2 style={{ width: "100%", float: "left", font: "600 30px Nib Pro", color: "#475168", marginTop: "70px" }}>{errorText}</h2>
                <Link to="/" style={{ width: "100%", float: "left", font: "400 16px Poppins, sans-serif", color: "#7D889A", textDecoration: "none" }}>
                  {langJson?.backhome || "Back to Home"}
                </Link>
              </div>
            </div>
          ) : !product || !activeVariant ? null : (
            <>
              

              <div className="topDetails prdtDtlsNew">
                {/* LEFT */}
                <div className="leftColumn">
                  <div className="product-detail">
                    <div className="mainImg zoomable-image" onClick={() => {
                      if (activeVideoIndex === null) {
                        setZoomImageIndex(activeImageIndex);
                        setZoomLevel(1);
                        setZoomModalOpen(true);
                      }
                    }}>
                      {activeVideoIndex !== null ? (
                        <div>
                          <video
                            width="100%"
                            height="511px"
                            controls
                            autoPlay
                            style={{ objectFit: "contain" }}
                          >
                            <source src={IMAGE_URL + activeVariant.videos[activeVideoIndex]} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      ) : (
                        <Slider {...sliderSettings} ref={sliderRef}>
                          {(activeVariant.medium_images?.length ? activeVariant.medium_images : activeVariant.images).map((img, i) => (
                            <div key={i}>
                              <img src={IMAGE_URL + img} alt="" />
                            </div>
                          ))}
                        </Slider>
                      )}
                    </div>

                    <div className="thumbImg">
                      <Slider {...thumbSettings} ref={thumbRef}>
                        {activeVariant.images.map((img, i) => (
                          <div
                            key={`img-${i}`}
                            className={`thumbnail ${activeImageIndex === i && activeVideoIndex === null ? "active" : ""
                              }`}
                            onClick={() => {
                              setActiveVideoIndex(null);
                              setTimeout(() => {
                                sliderRef.current?.slickGoTo(i);
                              }, 0);
                            }}
                          >
                            <img src={IMAGE_URL + img} alt="" />
                          </div>
                        ))}
                        {activeVariant.videos?.map((video, i) => (
                          <div
                            key={`vid-${i}`}
                            className={`thumbnail video-thumb ${activeVideoIndex === i ? "active" : ""}`}
                            onClick={() => {
                              setActiveVideoIndex(i);
                            }}
                          >
                            <svg width="42" height="42" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        ))}
                      </Slider>
                    </div>
                  </div>

                </div>

                {/* RIGHT */}
                <div className="rightColumn">
                  <p className="productTag">{product.tag}</p>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                    <h2>{activeVariant?.title}</h2>
                    <HeartIcon size={32} active={Boolean(currentWishlistId)} onClick={handleToggleCurrentWishlist} />
                  </div>
                  <div style={{ marginTop: "8px", fontSize: "14px", color: "#666", display: "flex", gap: "16px", flexWrap: "wrap" }}>
                    {activeVariant?.article_no && (
                      <div>
                        <span style={{ fontWeight: "300", color: "#000" }}>{activeVariant.article_no}</span>
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: "8px", fontSize: "14px", color: "#666", display: "flex", gap: "16px", flexWrap: "wrap" }}>

                    {/*product?.season && (
                      <div>
                        <span style={{ fontWeight: "500" }}>{langJson?.season || "Season"}:</span> {product.season}
                      </div>
                    )*/}
                    {/*product?.price && (
                      <div>
                        <span style={{ fontWeight: "500" }}>{langJson?.unitprice || "UVP"}:</span> {product.currency} {product.price}
                      </div>
                    )*/}
                  </div>
                  {/* {parseInt(product?.is_public) === 1 && (
                <h4>
                  {product.currency}
                  {product.sale_price}
                  {product.price && (
                    <span>
                      {product.currency}
                      {product.price}
                    </span>
                  )}
                </h4>
              )} */}
                  {parseInt(product?.is_public) === 1 && (
                    <h4>
                      {product.currency}
                      {product.sale_price && Number(product.sale_price) > 0
                        ? product.sale_price
                        : product.price}

                      {product.sale_price && Number(product.sale_price) > 0 && (
                        <span>
                          {product.currency}
                          {product.price}
                        </span>
                      )}
                    </h4>
                  )}

                  <div style={{ marginTop: "8px", fontSize: "14px", color: "#666", display: "flex", gap: "16px", flexWrap: "wrap" }}>
                    {activeVariant?.color && (
                      <div>
                        <span style={{ fontWeight: "300" }}>{langJson?.colour || "Color"}:</span> <span style={{ fontWeight: "500", color: "#000" }}>{activeVariant.color}</span>
                      </div>
                    )}
                  </div>

                  {/* VARIANTS */}
                  <div className="colorVariants  colorVariants--pdp">
                    {product.variants.map(v => (
                      <span
                        key={v.product_id}
                        className={`colorThumb ${activeVariant.product_id === v.product_id ? "active" : ""
                          }`}
                        onClick={() => handleVariantChange(v)}
                      >
                        <img src={IMAGE_URL + v.images[0]} alt={v.color} />
                      </span>
                    ))}
                  </div>

                  {/* STOCK TABLE */}
                  <div className="detailsCart">
                    <ul className="cartHdn">
                      <li>{langJson?.size}</li>
                      <li>{langJson?.instock}</li>
                      <li>{langJson?.quantity}</li>
                    </ul>

                    {selectedItems.map((item, i) => (
                      <ul key={i} className="cartDetails">
                        <li>{item.size}</li>
                        <li>{Math.max(0, item.stock)}</li>
                        <li>
                          <div className="quantity-selector">
                            <button onClick={() => updateQty(i, -1)}>
                              <img src={minusIcon} alt="" />
                            </button>
                            <span>{item.qty}</span>
                            <button onClick={() => updateQty(i, 1)}>
                              <img src={plusIcon} alt="" />
                            </button>
                          </div>
                        </li>
                      </ul>
                    ))}
                  </div>

                  {errorText && <span className="lgnStatus">{errorText}</span>}

                  <div className="detailBtns">
                    {/* <button className="cartBtn" onClick={handleAddCart} disabled={loading}>
                  {loading ? langJson?.pleasewait : langJson?.addtocart}
                </button> */}
                    {/* ADD TO CART – PUBLIC PRODUCT (ONLY IF VENDOR IS NOT ON FREE PLAN) */}
                    {parseInt(product?.is_public) === 1 && (
                      <button
                        className="cartBtn"
                        onClick={handleAddCart}
                        disabled={loading}
                      >
                        {loading ? langJson?.pleasewait : langJson?.addtocart}
                      </button>
                    )}

                    {/* REQUEST FOR PRICE – PRIVATE PRODUCT */}
                    {user?.role === "app_user" && parseInt(product?.is_public) !== 1 && product?.store?.active_plan !== 1 && (
                      <button
                        className="cartBtn cartBtn--secondary"
                        onClick={() => handleRequestPrice(product?.store?.id)}
                        disabled={loading}
                      >
                        {langJson?.requestforprice}
                      </button>
                    )}
                  </div>
                  {wishMsg && <span className="lgnStatus">{wishMsg}</span>}

                  <div className="additionalDesc">
                    <ul>
                      {/* SHIPPING & POLICIES */}
                      <li className="dropDiv">
                        <h3
                          className={openAccordion === "shipping" ? "active" : ""}
                          onClick={() =>
                            setOpenAccordion(openAccordion === "shipping" ? null : "shipping")
                          }
                        >
                          {langJson?.shippingpolicies || "Shipping & policies"}
                        </h3>

                        {openAccordion === "shipping" && (
                          <ul className="subSidebar">
                            {product.estimated_delivery && (
                              <li style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                                <svg className="svgicon fs-icon" focusable="false" viewBox="0 0 24 24" aria-hidden="true" role="presentation" style={{ width: "20px", height: "20px", color: "#333333" }}>
                                  <path d="M2.571 4.285A1.714 1.714 0 0 0 .857 6v15.428a1.714 1.714 0 0 0 1.714 1.714h18.857a1.714 1.714 0 0 0 1.715-1.714V6a1.714 1.714 0 0 0-1.715-1.715H18M.857 11.143h22.286M6 .858v6.857M18 .858v6.857M6 4.285h8.571" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                </svg>
                                <p>
                                  {langJson?.estimateddelivery || "Estimated delivery"}: {product.estimated_delivery}
                                </p>
                              </li>
                            )}
                            {product.ships_from && (
                              <li style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                                <svg className="svgicon fs-icon" focusable="false" viewBox="0 0 24 24" aria-hidden="true" role="presentation" style={{ width: "20px", height: "20px", color: "#333333" }}>
                                  <path d="M23.143 12A11.143 11.143 0 1 0 12 23.143M.857 12h22.286M12 23.143A11.143 11.143 0 0 0 23.143 12M12 23.143A19.24 19.24 0 0 0 16.285 12 19.24 19.24 0 0 0 12 .857m0 22.286A19.24 19.24 0 0 1 7.714 12 19.24 19.24 0 0 1 12 .857" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                </svg>
                                <p>
                                  {langJson?.shipsfrom || "Ships from"}: {product.ships_from}
                                </p>
                              </li>
                            )}
                            {product.eligible_for && (
                              <li style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                                <svg className="svgicon fs-icon" focusable="false" viewBox="0 0 24 24" aria-hidden="true" role="presentation" style={{ width: "20px", height: "20px", color: "#333333" }}>
                                  <path d="m7.8 11.8 3 3 6-6m6.343 9.2-.857-3.429-3.429.858m3.429-.858A11.31 11.31 0 0 1 12 22.286a10.32 10.32 0 0 1-9.663-6.772M.857 6l.857 3.429 3.429-.858m-3.429.858A11.63 11.63 0 0 1 12 1.714a10.32 10.32 0 0 1 9.664 6.772" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                </svg>
                                <p>
                                  {langJson?.eligiblefor || "Eligible For"}: {product.eligible_for}
                                </p>
                              </li>
                            )}

                          </ul>
                        )}
                      </li>

                      {/* DESCRIPTION */}
                      <li className="dropDiv">
                        <h3
                          className={openAccordion === "description" ? "active" : ""}
                          onClick={() =>
                            setOpenAccordion(openAccordion === "description" ? null : "description")
                          }
                        >
                          {langJson?.description}
                        </h3>

                        {openAccordion === "description" && (
                          <ul className="subSidebar">
                            <li>
                              <p>
                                {product.description}
                              </p>
                            </li>
                          </ul>
                        )}
                      </li>

                      {/* DETAILS */}
                      <li className="dropDiv">
                        <h3
                          className={openAccordion === "details" ? "active" : ""}
                          onClick={() =>
                            setOpenAccordion(openAccordion === "details" ? null : "details")
                          }
                        >
                          {langJson?.details || "Details"}
                        </h3>

                        {openAccordion === "details" && (
                          <ul className="subSidebar">
                            {product.details && (
                              <li>
                                <p style={{ whiteSpace: "pre-wrap" }}><strong>{langJson?.details || "Details"}:</strong></p>
                                <p style={{ whiteSpace: "pre-wrap" }}>{product.details}</p>
                              </li>
                            )}
                          </ul>
                        )}
                      </li>
                    </ul>
                  </div>


                </div>
              </div>

              {/* RELATED PRODUCTS */}
              <div className="tradePrdts tabTrde">
                {relatedProducts.length > 0 && (
                  <div className="featureDiv relatedDiv topNew">
                    <h2 class="storeTabHdn">{langJson?.relatedproducts}</h2>
                    <div className="productList">
                      <ul className="products new-products">
                        {relatedProducts.map(p => (
                          <ProductCard
                            key={p.order_no}
                            product={p}
                            handleAddWishlist={productId => addWishlistAndUpdate(productId, setRelatedProducts)}
                            handleRemoveWishlist={wishlistId => removeWishlistAndUpdate(wishlistId, setRelatedProducts)}
                          />
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Frequently Bought Together */}
                {crossSelling.length > 0 && (
                  <div className="featureDiv relatedDiv topNew">
                    <h2 className="storeTabHdn">Frequently bought together</h2>
                    <div className="productList">
                      <ul className="products new-products">
                        {crossSelling.map(p => (
                          <ProductCard
                            key={p.order_no}
                            product={p}
                            handleAddWishlist={productId => addWishlistAndUpdate(productId, setCrossSelling)}
                            handleRemoveWishlist={wishlistId => removeWishlistAndUpdate(wishlistId, setCrossSelling)}
                            showStore={true}
                            store={p?.store}
                          />
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
              <div className="tradePrdts">
                {relatedOthersProducts.length > 0 && (
                  <div className="featureDiv relatedDiv topNew">
                    <h2 class="storeTabHdn">{langJson?.similarproducts}</h2>
                    <div className="productList">
                      <ul className="products new-products">
                        {relatedOthersProducts.map(product => (
                          <ProductCard
                            key={product.order_no}
                            product={product}
                            handleAddWishlist={productId => addWishlistAndUpdate(productId, setRelatedOthersProducts)}
                            handleRemoveWishlist={wishlistId => removeWishlistAndUpdate(wishlistId, setRelatedOthersProducts)}
                            showStore={true}
                            store={product?.store}
                          />
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
              {recentlyViewedProducts?.length > 0 && (
                <div className="tradePrdts">
                  <div className="featureDiv relatedDiv topNew">
                    <h2 class="storeTabHdn">{langJson?.recentlyviewed || "Recently viewed"}</h2>
                    <div className="productList">
                      <ul className="products new-products">
                        {recentlyViewedProducts.map(product => (
                          <ProductCard
                            key={product.order_no}
                            product={product}
                            handleAddWishlist={productId => addWishlistAndUpdate(productId, setRecentlyViewedProducts)}
                            handleRemoveWishlist={wishlistId => removeWishlistAndUpdate(wishlistId, setRecentlyViewedProducts)}
                            showStore={true}
                            store={product?.store}
                          />
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Zoom Modal */}
      {zoomModalOpen && activeVariant && (
        <div className="zoom-modal-overlay" onClick={() => setZoomModalOpen(false)}>
          <button className="zoom-modal-close" onClick={() => setZoomModalOpen(false)}>×</button>
          <div className="zoom-modal" onClick={e => e.stopPropagation()}>

            <div className="zoom-image-container">
              {activeVideoIndex !== null ? (
                <video
                  width="100%"
                  height="100%"
                  controls
                  autoPlay
                  style={{ objectFit: "contain" }}
                >
                  <source src={IMAGE_URL + activeVariant.videos[activeVideoIndex]} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={IMAGE_URL + (activeVariant.medium_images?.[zoomImageIndex] || activeVariant.images[zoomImageIndex])}
                  alt=""
                />
              )}
            </div>

            <div className="zoom-thumbnails">
              {activeVariant.images.map((img, i) => (
                <div
                  key={`img-${i}`}
                  className={`zoom-thumb ${zoomImageIndex === i && activeVideoIndex === null ? "active" : ""}`}
                  onClick={() => {
                    setActiveVideoIndex(null);
                    setZoomImageIndex(i);
                  }}
                >
                  <img src={IMAGE_URL + img} alt="" />
                </div>
              ))}
              {activeVariant.videos?.map((video, i) => (
                <div
                  key={`vid-${i}`}
                  className={`zoom-thumb ${activeVideoIndex === i ? "active" : ""}`}
                  onClick={() => {
                    setActiveVideoIndex(i);
                  }}
                  style={{ backgroundColor: "#000", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProductDetails;

